import { harborUrl, satelliteUrl } from './urls';

export type HqGuideSection = {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
};

export type HqFaq = {
  question: string;
  answer: string;
};

export type HqGuideGroup = 'getting-started' | 'money-schedule' | 'records-court';

export type HqGuide = {
  slug: string;
  title: string;
  description: string;
  intro: string;
  outcome: string;
  group: HqGuideGroup;
  readMinutes: number;
  sections: HqGuideSection[];
  checklist: string[];
  faqs: HqFaq[];
  nextTool: { label: string; href: string; kind: 'hq' | 'satellite' | 'harbor' };
  harborNext?: { label: string; path: string };
};

export const GUIDE_GROUPS: { id: HqGuideGroup; label: string }[] = [
  { id: 'getting-started', label: 'Getting started' },
  { id: 'money-schedule', label: 'Money and schedule' },
  { id: 'records-court', label: 'Records and court' },
];

export const GUIDES: HqGuide[] = [
  {
    slug: 'first-week',
    title: 'First week after you separate',
    description:
      'A practical first-week checklist for housing, talking to the kids, money, and what to write down. Not legal advice.',
    intro:
      'You do not need a full plan this week. You need the kids stable, money from exploding, and a short record of what actually happened.',
    outcome: 'Leave with a five-item first-week checklist.',
    group: 'getting-started',
    readMinutes: 6,
    sections: [
      {
        heading: 'Housing',
        paragraphs: [
          'If it is safe, decide who stays in the home for the next several nights. Kids do better when their rooms and school run do not change on day one.',
          'If you leave, take IDs, medications, school contacts, and a change of clothes for the children. Tell one trusted person where you are.',
          'A temporary stay-in-place is not a custody decision. It is a way to keep bedtime from becoming a second crisis.',
        ],
      },
      {
        heading: 'Talking to the kids',
        paragraphs: [
          'If it is safe, tell them together. Keep it short. No blame, no adult details, no asking them to choose a side.',
        ],
        bullets: [
          'Both parents still love them.',
          'This is an adult problem, not theirs.',
          'The next few days may feel different, and you will tell them the plan as you know it.',
        ],
      },
      {
        heading: 'Money this week',
        paragraphs: [
          'Do not drain joint accounts, cancel health insurance, or hide paystubs. That creates a fight you do not need yet.',
          'Start a simple list of kid costs you already paid: school, daycare, prescriptions, activities. A split calculator is enough; you do not need an app on day one.',
        ],
      },
      {
        heading: 'What to write down',
        paragraphs: [
          'Same-day notes beat memory. Date, time, who was there, what you saw. Skip adjectives.',
          'Useful this week: who had the children each night, any missed pickup, and money you spent on them.',
        ],
      },
      {
        heading: 'What not to do this week',
        paragraphs: [
          'Do not use the children as messengers. Do not post the fight. Do not change the school or the doctor unless a professional says it is unsafe to stay.',
          'A long manifesto text feels like action. It usually becomes an exhibit. One short, factual message is enough.',
        ],
      },
      {
        heading: 'Safety first',
        paragraphs: [
          'If you do not feel safe, skip housing negotiations and paperwork. Get help first. In the US you can call or text 988, or 1-800-799-7233 for the domestic violence hotline.',
        ],
      },
    ],
    checklist: [
      'Decide who stays in the home this week, and keep the kids’ rooms stable if you can.',
      'Tell the children together if it is safe: short, no blame, both parents still love them.',
      'Do not drain joint accounts or cancel insurance. Start a simple list of kid costs.',
      'Write down exchanges, expenses, and anything you observed. Facts only, same day.',
      'If you do not feel safe, skip paperwork and get help first.',
    ],
    faqs: [
      {
        question: 'Do I need a lawyer this week?',
        answer:
          'Not automatically. Get help now if you are unsafe or there is a protective order. A lawyer is usually needed when custody, support, or property is already a fight. See the lawyer guide on this site.',
      },
      {
        question: 'Should I tell the school this week?',
        answer:
          'Tell the school if pickup people or emergency contacts change. You do not need to narrate the relationship. A short facts-only note is enough.',
      },
      {
        question: 'What if we still live in the same house?',
        answer:
          'Agree, if it is safe, who sleeps where and who does the school run for the next few nights. Write that down. A shared roof is not a reason to skip a short record.',
      },
    ],
    nextTool: {
      label: 'When you actually need a lawyer',
      href: '/guides/need-a-lawyer',
      kind: 'hq',
    },
  },
  {
    slug: 'document-now',
    title: 'How to document before anyone cares',
    description:
      'A facts-only way to log exchanges, expenses, and missed handoffs before a court or lawyer asks. Not legal advice.',
    intro:
      'People start documenting when a fight is already loud. The useful record is the quiet one you wrote the same day, in plain language.',
    outcome: 'Leave knowing what to log, and what to leave out.',
    group: 'records-court',
    readMinutes: 6,
    sections: [
      {
        heading: 'Facts only',
        paragraphs: [
          'Write the date, time, who was there, and what happened. “Pickup was 6:40, fifteen minutes late” is a record. “They are always disrespectful” is not.',
          'If you would not want a judge to hear you read it aloud, rewrite it until you would.',
        ],
      },
      {
        heading: 'What is worth logging',
        bullets: [
          'Exchanges: planned time, actual time, who showed up, who the children left with.',
          'Kid expenses: amount, what it was for, who paid, whether you asked to be repaid.',
          'Medical and school: appointments kept or missed, who was told.',
          'Missed parenting time: what was scheduled, what happened, any message you sent.',
        ],
      },
      {
        heading: 'Write it the same day',
        paragraphs: [
          'A two-line note on the day it happened is stronger than a long recap a month later. You are not building a novel. You are dating a fact.',
        ],
      },
      {
        heading: 'What not to do',
        paragraphs: [
          'Do not secretly record a conversation in a two-party consent state without checking the law. Do not put the children in the middle as witnesses. Do not edit old notes to sound angrier.',
        ],
      },
      {
        heading: 'Private vs shared',
        paragraphs: [
          'A private journal is for you. A shared expense list or visitation log is for facts both parents can see. Keep those two piles separate.',
          'If a court order already names an app for messages or expenses, that app is the official channel. Your private file does not replace it.',
        ],
      },
    ],
    checklist: [
      'Log the date, time, who was there, and what happened. Skip adjectives.',
      'Keep exchange notes, kid expenses, medical visits, and school events.',
      'Write it the same day. Memory fades; a short note holds up better.',
      'Do not secretly record in a two-party consent state without checking the law.',
      'A private journal is for you. Shared logs are for facts both parents can see.',
    ],
    faqs: [
      {
        question: 'Is a notes app good enough?',
        answer:
          'Yes, if you date the note the same day and do not go back to rewrite it angrier. A printable log is useful when you want one page per exchange.',
      },
      {
        question: 'Should I send the log to the other parent?',
        answer:
          'A shared expense list can be useful. A private journal of what you observed usually should stay private unless counsel tells you otherwise.',
      },
      {
        question: 'What if I already have months of angry texts?',
        answer:
          'Start clean today. Do not edit the old thread. Future notes should be facts only. The old thread is already what it is.',
      },
    ],
    nextTool: {
      label: 'Print a visitation log',
      href: satelliteUrl('visitationtracker.com'),
      kind: 'satellite',
    },
    harborNext: {
      label: 'Keep a private journal in Harbor',
      path: '/',
    },
  },
  {
    slug: 'need-a-lawyer',
    title: 'When you actually need a lawyer',
    description:
      'A plain-language list of when self-help is usually enough and when to get counsel. Not legal advice.',
    intro:
      'A lawyer is not a default purchase. It is the right next step when safety, a fight over the kids or money, or an existing court order makes DIY risky.',
    outcome: 'Leave knowing whether this is a forms problem or a counsel problem.',
    group: 'getting-started',
    readMinutes: 5,
    sections: [
      {
        heading: 'Get help now',
        paragraphs: [
          'If you do not feel safe, if there is a protective order, or if the other parent has threatened you or the children, this is not a forms problem. Get local help. In the US: 988, or 1-800-799-7233.',
        ],
      },
      {
        heading: 'Usually lawyer territory',
        bullets: [
          'You do not agree on custody, parenting time, or support.',
          'There are hidden assets, a business, or a retirement fight.',
          'Someone is in bankruptcy, or there is already an open family-law case.',
          'A court order already names a specific app or program you must use.',
          'There is a relocation, an interstate issue, or a parent in the military with complex rules.',
        ],
      },
      {
        heading: 'When self-help may be enough',
        paragraphs: [
          'If you both want the same outcome, can sign together, and the terms on kids and money are mostly settled, a state checklist or an uncontested packet can be a starting point. A court still decides. This is not legal advice.',
        ],
      },
      {
        heading: 'Humans who are not your lawyer',
        paragraphs: [
          'A coparenting coach can help with communication. They do not replace counsel, and they do not file for you.',
        ],
      },
    ],
    checklist: [
      'Get help now if you do not feel safe, or if there is an active protective order.',
      'A lawyer is usually needed when custody, support, or property is contested.',
      'Hidden assets, bankruptcy, or an open family-law case are lawyer territory.',
      'If a court order names a specific app, follow that order.',
      'If you both agree on the major terms, a self-help packet may be enough. This is not legal advice.',
    ],
    faqs: [
      {
        question: 'Can I start with a free checklist and hire later?',
        answer:
          'Often yes, if you both still agree. Stop and get counsel if a fight starts over the kids, money, or an existing order.',
      },
      {
        question: 'Is a coach a cheaper lawyer?',
        answer:
          'No. A coach can help you write a calmer message. They cannot file, negotiate a decree, or tell you what a judge will do.',
      },
      {
        question: 'What if we already have an order?',
        answer:
          'Follow the order, including any named app. Changing the official channel on a weekend is how people get a compliance problem.',
      },
    ],
    nextTool: {
      label: 'Browse coaches',
      href: harborUrl('/coaches'),
      kind: 'harbor',
    },
    harborNext: {
      label: 'See if a Harbor Divorce packet may fit',
      path: '/divorce',
    },
  },
  {
    slug: 'named-app',
    title: 'My order says OurFamilyWizard (or similar)',
    description:
      'If a court order names OurFamilyWizard, TalkingParents, or another app, use that app. Here is what you can still do solo. Not legal advice.',
    intro:
      'If a judge or a signed order names an app, that app is the official channel for whatever the order covers. Switching both parents because something else is cheaper is how you get a compliance problem.',
    outcome: 'Leave knowing what must stay on the named app, and what can stay private.',
    group: 'records-court',
    readMinutes: 5,
    sections: [
      {
        heading: 'Use the named app',
        paragraphs: [
          'Messages, exchanges, or expenses the order says must go through that app should go through that app. Ask your lawyer if a line is unclear. Do not guess.',
        ],
      },
      {
        heading: 'What you can still do on your own',
        paragraphs: [
          'You can keep a private, facts-only record of what you observed. You can track kid costs you paid even if the other parent will not join a second tool. You can write a parenting-time calendar for yourself so you stop missing days.',
          'None of that replaces the named app. It is your own file.',
        ],
      },
      {
        heading: 'Do not move the official channel',
        paragraphs: [
          'Do not tell the other parent to abandon the named app. Do not treat a new login as the court-approved record. If you want to switch later, that is a modification conversation with counsel, not a weekend project.',
        ],
      },
    ],
    checklist: [
      'Use the app named in the order for anything the order covers.',
      'Do not move both parents off that app because a different tool is cheaper.',
      'You can still keep a private record of what you observed.',
      'Ask counsel before you treat a new app as the official channel.',
      'If the order is later modified, you can switch then.',
    ],
    faqs: [
      {
        question: 'Can I still use a free calendar for myself?',
        answer:
          'Yes, as your own reminder. It is not the official record if the order named a different channel.',
      },
      {
        question: 'What if the other parent refuses the named app?',
        answer:
          'You still use it for what the order covers. Document the refusal in facts. Ask counsel before you invent a substitute channel.',
      },
      {
        question: 'Is Harbor a replacement for OurFamilyWizard?',
        answer:
          'No, not if an order names another app. Harbor can still be a private journal or a solo calendar. It does not override a court-named channel.',
      },
    ],
    nextTool: {
      label: 'Start a facts-only documentation checklist',
      href: '/guides/document-now',
      kind: 'hq',
    },
  },
  {
    slug: 'propose-a-schedule',
    title: 'How to propose a parenting schedule',
    description:
      'How to offer a 2-2-3, week-on/week-off, or weekend rotation without starting a fight. Not legal advice.',
    intro:
      'A schedule proposal works when it is specific, short, and about the kids’ week, not about who is the better parent. Send a calendar, not a speech.',
    outcome: 'Leave with a one-page way to offer 2-2-3 or week-on/week-off.',
    group: 'money-schedule',
    readMinutes: 7,
    sections: [
      {
        heading: 'Name the rotation in one line',
        paragraphs: [
          'Start with the pattern, the first exchange day, and the exchange time. “Week-on/week-off, Friday 6pm, starting May 2” is a proposal. “I need more time because you never show up” is a fight.',
        ],
        bullets: [
          '2-2-3: two days, two days, a three-day weekend. Common when kids are young and both homes are close.',
          'Week-on/week-off: seven days each. Fewer midweek handoffs. Harder if school is far from one home.',
          'Every weekend / every other weekend: more common when one parent has the school week.',
        ],
      },
      {
        heading: 'Anchor it to the kids, not to fairness rhetoric',
        paragraphs: [
          'Say what the week looks like for school, bedtime, and the activity they already have. A judge, a mediator, or the other parent can react to a week. They cannot usefully react to “50/50 or I walk.”',
        ],
      },
      {
        heading: 'Send a calendar, then stop talking',
        paragraphs: [
          'Build the rotation, export a file or a three-month preview, and send that. One short paragraph is enough: the pattern, the first date, the exchange place, and that you can adjust holidays later.',
          'Do not stack three alternative speeches in the same text. One clear option, then wait.',
        ],
      },
      {
        heading: 'What to leave for later',
        paragraphs: [
          'Holidays, summers, and makeup time belong in a parenting plan. Do not hold the weekly rotation hostage to Christmas. Get the school-year week standing first.',
        ],
      },
      {
        heading: 'If they reject it',
        paragraphs: [
          'Ask which part does not work: the exchange day, the overnight count, or the travel. A specific objection is something you can change. “No” with no alternative is a record that you offered a written plan.',
        ],
      },
    ],
    checklist: [
      'Name the rotation, first exchange day, time, and place in one sentence.',
      'Build a three-month preview so both of you can see the week, not just the slogan.',
      'Send the calendar with one short paragraph. Do not add a speech.',
      'Leave holidays and summers for a parenting-plan conversation.',
      'If they say no, ask which part fails: day, overnights, or travel.',
    ],
    faqs: [
      {
        question: 'Is 50/50 required?',
        answer:
          'No. Many families use it. Distance, work shifts, and the children’s ages matter more than a slogan. This is not legal advice; your state has its own factors.',
      },
      {
        question: 'Should I send this through a court-named app?',
        answer:
          'If an order names an app for messages, send the proposal there. A side channel can become a compliance problem.',
      },
      {
        question: 'What if we cannot agree on anything?',
        answer:
          'Write down that you offered a dated rotation. Then it is usually counsel or a mediator, not a longer text thread.',
      },
    ],
    nextTool: {
      label: 'Build the rotation and export a calendar',
      href: satelliteUrl('custodyscheduleapp.com'),
      kind: 'satellite',
    },
    harborNext: {
      label: 'Keep the shared week in Harbor',
      path: '/',
    },
  },
  {
    slug: 'split-expenses',
    title: 'How to split kid expenses fairly',
    description:
      'What counts as a kid cost, how to split it, and how to ask for repayment without a fight. Not legal advice.',
    intro:
      'Most money fights are not about the amount. They are about a screenshot with no date, no receipt, and a paragraph of blame. A split is a number plus a one-line ask.',
    outcome: 'Leave able to split one cost and send a repayment note.',
    group: 'money-schedule',
    readMinutes: 7,
    sections: [
      {
        heading: 'What usually counts',
        paragraphs: [
          'Uninsured medical, agreed activities, school fees, and childcare are the usual shared pile. Clothes the child already has at one house, and extras you did not discuss, are where people start fighting.',
        ],
        bullets: [
          'Write the date, the amount, what it was for, and who paid.',
          'Keep the receipt or a photo of it.',
          'Say whether you already asked to be repaid.',
        ],
      },
      {
        heading: 'Pick a split before the next receipt',
        paragraphs: [
          '50/50 is simple. Income pro-rata is common when paychecks are far apart. A custom percent is fine if you both can say the number out loud.',
          'Guideline child support is a different question. An estimate can tell you the ballpark. It does not replace an order.',
        ],
      },
      {
        heading: 'Ask once, in writing, without adjectives',
        paragraphs: [
          '“Soccer $84 on March 3, receipt attached, 50/50 is $42. Please send by March 17.” That is a request. “You never pay for anything and I am done covering for you” is a different document.',
        ],
      },
      {
        heading: 'What not to do',
        paragraphs: [
          'Do not withhold the child because a reimbursement is late. Do not add last year’s grudges to this receipt. Do not post the Venmo fight.',
        ],
      },
      {
        heading: 'If they ignore it',
        paragraphs: [
          'Send the same facts once more. Then keep the unpaid line on a list. A running ledger is more useful than a weekly argument. If an order already covers extras, follow that order.',
        ],
      },
    ],
    checklist: [
      'Write date, amount, what it was for, who paid, and attach the receipt.',
      'Agree a split (50/50, pro-rata, or a number you can both say) before the next cost.',
      'Ask once in one factual sentence with a due date.',
      'Do not withhold parenting time over an unpaid receipt.',
      'If they ignore it, log the unpaid line and follow any existing order.',
    ],
    faqs: [
      {
        question: 'Does child support already cover soccer?',
        answer:
          'Sometimes extras are on top of support; sometimes they are not. Read your order. If there is no order, agree the extra in writing before you spend.',
      },
      {
        question: 'What if I already paid and they refuse?',
        answer:
          'Keep the receipt and the ask. A calculator letter is enough for the file. Counsel can tell you whether your state treats that as reimbursable.',
      },
      {
        question: 'Should both parents see the same list?',
        answer:
          'A shared expense list reduces “I never saw that.” A private journal of the fight does not belong on that list.',
      },
    ],
    nextTool: {
      label: 'Split an expense and copy a reimbursement letter',
      href: satelliteUrl('coparentmoney.com'),
      kind: 'satellite',
    },
    harborNext: {
      label: 'Keep a shared expense ledger in Harbor',
      path: '/',
    },
  },
  {
    slug: 'write-a-message',
    title: 'How to write a coparenting message',
    description:
      'A facts-only way to text or email the other parent: short, dated, and usable later. Not legal advice.',
    intro:
      'The message you send at 11pm is often the one that gets read aloud later. Write as if a third person will see it, because they might.',
    outcome: 'Leave with a short template you can reuse tonight.',
    group: 'getting-started',
    readMinutes: 6,
    sections: [
      {
        heading: 'Facts, not a verdict',
        paragraphs: [
          'A usable message has a date, a request, and a time. “Can you do Thursday pickup at 5:30 at the library?” is a message. “As usual you are making this impossible” is a verdict.',
        ],
      },
      {
        heading: 'A simple shape: BIFF-ish',
        paragraphs: [
          'Brief. Informative. Friendly enough. Firm about the logistics. You do not need the acronym. You need fewer words.',
        ],
        bullets: [
          'One topic per message.',
          'The child, the time, the place.',
          'No diagnosis of their character.',
          'No essays, no all-caps, no late-night pile-on.',
        ],
      },
      {
        heading: 'A template you can copy',
        paragraphs: [
          '“Thursday 5:30 pickup at the library main door. I will have Maya’s backpack. Confirm if that still works.”',
          'If you need to document a no-show, keep it equally flat: “You were not at the library at 5:30. I waited until 5:50. Maya is with me tonight.”',
        ],
      },
      {
        heading: 'Where to send it',
        paragraphs: [
          'If a court order names an app for messages, send it there. A side text can become “you went around the order.” If there is no order, one channel is better than three.',
        ],
      },
      {
        heading: 'What not to send',
        paragraphs: [
          'Do not send voice notes you would not transcribe. Do not involve the children as couriers. Do not reply to bait with a matching paragraph. Silence plus a later factual note is allowed.',
        ],
      },
    ],
    checklist: [
      'One topic: the child, the time, the place.',
      'No character diagnosis, all-caps, or late-night essays.',
      'Use the court-named app if an order requires it.',
      'For a no-show, write the time you waited and where the child is now.',
      'If the thread is already on fire, stop and write the next note the next morning.',
    ],
    faqs: [
      {
        question: 'What if they only fight in text?',
        answer:
          'Answer the logistics. Ignore the rest, or say you will stick to pickup times. A matching essay rarely ends the thread.',
      },
      {
        question: 'Can I write a longer letter once?',
        answer:
          'A long letter is usually for counsel or for you. Day-to-day logistics stay short so they can be used later without a highlight reel of adjectives.',
      },
      {
        question: 'Should I bcc someone?',
        answer:
          'Do not secretly loop in the children or their teachers. If you need a third adult, that is usually a lawyer, a coach, or a mediator, not a group chat.',
      },
    ],
    nextTool: {
      label: 'How to document before anyone asks',
      href: '/guides/document-now',
      kind: 'hq',
    },
    harborNext: {
      label: 'Keep messages in one place in Harbor',
      path: '/',
    },
  },
  {
    slug: 'missed-exchange',
    title: 'When the other parent misses an exchange',
    description:
      'What to write down when a pickup is late or does not happen, and what not to do in the parking lot. Not legal advice.',
    intro:
      'A missed exchange feels like an emergency. The useful move is smaller: keep the child safe, write the times, and do not turn the parking lot into a scene.',
    outcome: 'Leave with a facts-only note you can write in the car.',
    group: 'records-court',
    readMinutes: 6,
    sections: [
      {
        heading: 'The child first',
        paragraphs: [
          'If you have the child and the other parent does not arrive, stay put for a reasonable window if it is safe, then go home. Tell the child a boring sentence: the plan changed, they are safe, you will sort the adult part.',
          'If you are the one waiting and the child does not arrive, call once. Then write the times. Do not chase through the other house unless a professional says there is danger.',
        ],
      },
      {
        heading: 'Write five facts',
        bullets: [
          'What was scheduled: date, time, place.',
          'When you arrived.',
          'How long you waited.',
          'Any message you sent or received.',
          'Where the child slept that night.',
        ],
      },
      {
        heading: 'The message',
        paragraphs: [
          'Send one factual note on the official channel if you have one. “I was at the library from 5:28 to 5:52. No pickup. Maya is at my house tonight.” Then stop.',
        ],
      },
      {
        heading: 'What not to do',
        paragraphs: [
          'Do not withhold the next visit as punishment unless an order or a professional tells you the child is unsafe. Do not film the other parent for leverage. Do not make the child narrate what happened for a recording.',
        ],
      },
      {
        heading: 'A pattern vs a one-off',
        paragraphs: [
          'One late pickup is a note. A month of no-shows is a list. The list is what counsel or a court can use. The speech you wrote in the car is not.',
        ],
      },
    ],
    checklist: [
      'Keep the child safe and keep the explanation boring.',
      'Write scheduled time, arrival, wait, messages, and where the child slept.',
      'Send one factual note on the official channel, then stop.',
      'Do not punish the next visit or film the parking lot for leverage.',
      'If it becomes a pattern, keep the list, not a recap essay.',
    ],
    faqs: [
      {
        question: 'How long should I wait?',
        answer:
          'There is no universal number. Fifteen to thirty minutes is a common window if it is safe. Write the actual minutes you waited.',
      },
      {
        question: 'Should I call the police?',
        answer:
          'Call if the child is missing or you believe they are in danger. A late pickup by itself is usually a record, not a 911 event. Follow local guidance if you are unsure.',
      },
      {
        question: 'What if they say traffic every time?',
        answer:
          'Write the stated reason and the actual arrival. You do not need to argue about traffic in the thread.',
      },
    ],
    nextTool: {
      label: 'Print a missed-exchange log',
      href: satelliteUrl('visitationtracker.com'),
      kind: 'satellite',
    },
    harborNext: {
      label: 'Keep a private journal in Harbor',
      path: '/',
    },
  },
  {
    slug: 'parenting-plan',
    title: 'What belongs in a parenting plan',
    description:
      'The pieces of a usable parenting plan: time, holidays, money, communication, and decisions. Not legal advice.',
    intro:
      'A parenting plan is not a love letter and it is not a novel. It is the boring document that tells both houses what happens on Tuesday.',
    outcome: 'Leave knowing the sections to fill before you file or hire.',
    group: 'money-schedule',
    readMinutes: 8,
    sections: [
      {
        heading: 'The weekly rotation',
        paragraphs: [
          'Who has which nights in a normal school week, exchange time, and exchange place. If you cannot say this in one paragraph, the rest of the plan will not save you.',
        ],
      },
      {
        heading: 'Holidays and school breaks',
        paragraphs: [
          'Alternate the big days or split them by hours. Name the years (even/odd) so you are not renegotiating Thanksgiving in the grocery line. Summers need their own line: week-on/week-off, a block, or the school-year pattern.',
        ],
      },
      {
        heading: 'Decisions',
        paragraphs: [
          'School, medical, and activities are the usual three. Say who decides routine care versus who must be asked before a new sport or a new prescription. “We will be reasonable” is not a clause.',
        ],
      },
      {
        heading: 'Money',
        paragraphs: [
          'Support is often a separate order. The plan can still say how uninsured medical and activities are split, who carries insurance, and how reimbursement is requested.',
        ],
      },
      {
        heading: 'Communication',
        paragraphs: [
          'How parents talk to each other, and how they talk to the school. If a court later names an app, that line wins. Until then, one channel beats three.',
        ],
      },
      {
        heading: 'What to leave out',
        paragraphs: [
          'Do not write a history of the relationship. Do not include insults dressed up as “background.” Do not promise a personality change. A plan is logistics.',
        ],
      },
    ],
    checklist: [
      'Write the school-week rotation, exchange time, and place.',
      'Name holidays and summers with even/odd years or a split by hours.',
      'Say who decides school, medical, and new activities.',
      'Say how extras are split and how repayment is requested.',
      'Name one communication channel. Skip the relationship history.',
    ],
    faqs: [
      {
        question: 'Is a parenting plan the same as a court order?',
        answer:
          'Not until a court adopts it or you both sign something your state treats as binding. A draft is still useful because it makes the disagreements visible.',
      },
      {
        question: 'Do we need a lawyer to draft one?',
        answer:
          'If you disagree on the kids or money, usually yes. If you already agree, a structured draft can be a starting point. A court still decides. This is not legal advice.',
      },
      {
        question: 'Can we change it later?',
        answer:
          'Most families do, at least for holidays. Changing an order is a legal step. Changing an informal draft is a conversation. Do not treat those as the same thing.',
      },
    ],
    nextTool: {
      label: 'Walk through a parenting-plan PDF',
      href: harborUrl('/parenting-plan/builder'),
      kind: 'harbor',
    },
    harborNext: {
      label: 'Start the Harbor parenting-plan builder',
      path: '/parenting-plan/builder',
    },
  },
];

export const GUIDES_BY_SLUG: Record<string, HqGuide> = Object.fromEntries(
  GUIDES.map((guide) => [guide.slug, guide]),
);
